"use client";
import { useCallback, useState } from "react";

import Button from "@/components/common/Button";
import { PostDetailType } from "@/types/post";
import { formatUrl } from "@/utils/stringUtils";

import PostIndex from "./PostIndex";

// 동적 버튼 관리를 위한 타입 정의
interface ButtonConfig {
  text: string;
  url: string;
  icon?: string;
  className?: string;
}

const shadowCss = `
      body {
        line-height: 1.6;
      }
      .page-body > *:first-child {
        margin-top: 0;
      }
      * {
        margin: 0;
        color: #343434;
        word-break: break-all;
      }
      p,
      div:not(.page-body),
      ol, ul {
        font-size: 1.2rem;
        letter-spacing: -0.3px;
        font-weight: 300;
        margin: 1.5rem 0;
        line-height: 1.6;
      }
      li {
        font-size: 1.2rem;
        letter-spacing: -0.3px;
        font-weight: 300;
        margin: 1rem 0;
      }
      h2 {
        font-size: 2rem;
        font-weight: 700;
        margin: 4rem 0 2rem;
        line-height: 1.2;
	      scroll-margin-top: calc(80px + 15px);
      }
      h3 {
        font-size: 1.6rem;
        font-weight: 700;
        margin: 1.6rem 0;
        line-height: 1.4;
      }
      h4 {
        font-size: 1.4rem;
        font-weight: 600;
        margin: 1.5rem 0;
        line-height: 1.4;
      }
      h2 + h3, h2 + h4, h3 + h4 {
        margin-top: -0.5rem;
      }
      h2, h3, h4, strong {
        color: #232323;
      }
      figure {
        margin: 2rem 0;
      }
      img {
        width: 100%;
        max-width: 36rem;
      }
      strong {
        font-weight: 500;
      }
      figure.callout {
        padding: 1rem 2.5rem;
        background-color: #f7f7f7;
        border-radius: 13px;
      }
      figure.callout > div > *:first-child {
        margin-top: 0;
      }
      figure.callout > div > *:last-child {
        margin-bottom: 0;
      }
      figure:has(img)
      figcaption {
        color: #8e8e93;
        font-size: 1rem;
        margin-top: 0.375rem;
      }
      figure:has(img) {
        text-align: center;
        margin: 3rem 0;
      }
      ul, ol {
        padding-inline-start: 20px;
      }
      a, a * {
        color: #1346d5 !important;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
      .question {
        font-size: 1.5rem;
        display: flex;
        color: #000000;
        border-bottom: 1px solid #e2e2e2;
        padding: 0.5em 0;
        gap: 0.6em;
      }
      .question::before {
        content: '🎤';
      }
      .smyang,
      .eschoi,
      .hwjeong {
        padding: 0.2rem 1rem;
        border-radius: 3px;
      }
      .smyang {
        background-color: rgba(255, 102, 102, 0.25);
      }
      .eschoi {
        background-color: rgba(102, 255, 145, 0.25);
      }
      .hwjeong {
        background-color: rgba(107, 102, 255, 0.25);
      }

      @media screen and (max-width: 768px) {
        figure.callout {
          padding: 0.5rem 1rem;
        }
      }
      `;

function PostContent({ slug, content }: { slug: PostDetailType["slug"]; content: PostDetailType["content"] }) {
  const [indexList, setIndexList] = useState<string[]>([]);

  // 동적 버튼 설정 관리 로직
  const getButtonConfig = useCallback((): ButtonConfig | null => {
    if (!slug) return null;

    // slug에 따른 다양한 버튼 설정
    if (slug.includes("ebook")) {
      return {
        text: "📄 E-Book 다운로드",
        url: "https://featpaper.com/l/sMwaR4lg",
        icon: "📄",
        className: "bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors",
      };
    }

    if (slug.includes("download")) {
      return {
        text: "⬇️ 자료 다운로드",
        url: "https://featpaper.com/l/sMwaR4lg",
        icon: "⬇️",
        className: "bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors",
      };
    }

    if (slug.includes("강남언니")) {
      return {
        text: "👉 우리 팀에 바로 적용하기",
        url: "/request/",
        icon: "👉",
        className: "bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors",
      };
    }

    if (slug.includes("스타-인재-찾기") || slug.includes("이스트에이드")) {
      return {
        text: "👉 숨은 인재 요청하기",
        url: "/request/",
        icon: "👉",
        className: "bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors",
      };
    }

    return null;
  }, [slug]);

  // 재사용 가능한 버튼 컴포넌트
  const DynamicButton = ({ config }: { config: ButtonConfig }) => (
    <div className="flex justify-center">
      <Button href={config.url} target="_blank" rel="noopener noreferrer" className="inline-block">
        {config.text}
      </Button>
    </div>
  );

  const attachShadow = useCallback(
    (contentDiv: HTMLDivElement) => {
      if (content && contentDiv && !contentDiv.shadowRoot) {
        const shadow = contentDiv.attachShadow({ mode: "open" });
        shadow.innerHTML = `
          <style>${shadowCss}</style>
          ${content}
        `;

        getH2ElementsAndSetId(shadow);
      }
    },
    [content],
  );

  const getH2ElementsAndSetId = (shadow: ShadowRoot) => {
    const temp: string[] = [];
    const h2List = shadow.querySelectorAll("h2");
    h2List.forEach((h2) => {
      h2.id = formatUrl(h2.innerText);
      temp.push(h2.innerText);
    });
    setIndexList(temp);
  };

  const buttonConfig = getButtonConfig();

  return (
    <div className="relative">
      {/* 동적 버튼 - 콘텐츠 상단 */}
      {buttonConfig && <DynamicButton config={buttonConfig} />}

      <div id="content" ref={attachShadow} className="font-pretendard"></div>
      <PostIndex indexList={indexList} />

      {/* 동적 버튼 - 콘텐츠 하단 */}
      {buttonConfig && <DynamicButton config={buttonConfig} />}
    </div>
  );
}

export default PostContent;
